import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { printVisitClinicInvoice } from '@/services/main/visitClinicInvoice';
import type { VisitClinicInvoicePrintPayload } from '@/services/main/visitClinicInvoice/types';
import { getApiErrorMessageAsync } from '@/utils/apiError';

/**
 * Mutation hook for printing visit clinic invoice PDF (来院用領収書).
 *
 * - Calls clinic-scoped API and receives a PDF Blob.
 * - Opens PDF in a new tab.
 * - Shows a toast on failure, with the backend's message read out of the Blob body
 *   (e.g. 「該当するデータはありませんでした。」). Callers that show the error themselves
 *   (the 領収明細編集 modal shows it in a dialog) pass `{ rethrow: true }`.
 */
export function useVisitClinicInvoicePrintMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    VisitClinicInvoicePrintPayload
  >({
    mutationFn: payload => printVisitClinicInvoice(tenant, clinicNumber, payload),
    onSuccess: (_blob, payload) => {
      // 雛形 (format) print does not touch visit rows; only refetch when receipts/monthly run.
      // A2 lists under ['continuation'], R2 under ['visitClinicInvoice'] — refresh both so
      // the printed rows show 「済」 without a reload (SCR-182 BUG-05).
      if (payload.print_type !== 'format') {
        queryClient.invalidateQueries({ queryKey: ['continuation'] });
        queryClient.invalidateQueries({ queryKey: ['visitClinicInvoice'] });
      }
    },
  });

  const triggerPrint = async (
    payload: VisitClinicInvoicePrintPayload,
    options?: { rethrow?: boolean }
  ) => {
    const tab = window.open('', '_blank');

    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);

      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      if (options?.rethrow) throw error;
      toast.error(await getApiErrorMessageAsync(error, '印刷に失敗しました。'));
    }
  };

  return { triggerPrint, isLoading };
}
